import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Calculator } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import heroBg from "@/assets/hero-background.webp.asset.json";

const SLIDE_INTERVAL = 10000;

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const Hero = () => {
  const { t } = useTranslation();
  const slides = [
    {
      key: "s0",
      title1: t("hero.slide0_title_1"),
      highlight: t("hero.slide0_title_highlight"),
      title2: t("hero.slide0_title_2"),
      subtitle: t("hero.slide0_subtitle"),
    },
    {
      key: "s1",
      title1: t("hero.title_1"),
      highlight: t("hero.title_highlight"),
      title2: t("hero.title_2"),
      subtitle: t("hero.subtitle"),
    },
    {
      key: "s3",
      title1: t("hero.slide3_title_1"),
      highlight: t("hero.slide3_title_highlight"),
      title2: t("hero.slide3_title_2"),
      subtitle: t("hero.slide3_subtitle"),
    },
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), SLIDE_INTERVAL);
    return () => window.clearInterval(id);
  }, [slides.length]);

  const current = slides[index];

  return (
    <section className="overflow-hidden relative min-h-[600px] md:min-h-[680px] lg:min-h-[760px] flex items-center">
      {/* Background image covering the entire hero */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroBg.url}
          alt=""
          className="w-full h-full object-cover object-center"
          width={1920}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      </div>

      <div className="container relative z-10 pt-24 pb-12 md:pt-32 md:pb-20 w-full">
        <div className="max-w-xl lg:max-w-2xl">
          <h1 className="sr-only">{t("hero.h1")}</h1>

          {/* Text block with fixed height so buttons stay in place */}
          <div className="min-h-[360px] sm:min-h-[280px] md:min-h-[300px] lg:min-h-[400px] flex flex-col">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5 }}
                className="flex-1 flex flex-col justify-center"
              >
                <p className="text-fluid-hero font-bold mb-5 md:mb-6 text-gray-900 text-balance" aria-hidden="true">
                  {current.title1} <span className="text-primary">{current.highlight}</span>
                  {current.title2 ? <> {current.title2}</> : null}
                </p>
                <p className="text-fluid-lead text-gray-700 max-w-[52ch] text-pretty line-clamp-3 md:line-clamp-4">
                  {current.subtitle}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Buttons - fixed position */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4 md:mt-5">
            <Button
              size="lg"
              className="shadow-brand text-base px-6 sm:px-8 w-full sm:w-auto"
              onClick={() => scrollTo("pricing")}
            >
              {t("hero.cta_plans")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-base px-6 sm:px-8 w-full sm:w-auto bg-white/80 backdrop-blur-sm"
              onClick={() => scrollTo("pricing")}
            >
              <Calculator className="w-4 h-4 mr-2" />
              {t("hero.cta_calc")}
            </Button>
          </div>

          {/* Trust - fixed position */}
          <p className="text-xs sm:text-sm text-muted-foreground mt-5 sm:mt-6">{t("hero.trust")}</p>

          {/* Dots - fixed position */}
          <div className="flex gap-2 mt-6 sm:mt-8">
            {slides.map((s, i) => (
              <button
                key={s.key}
                type="button"
                aria-label={`Slide ${i + 1}`}
                onClick={() => setIndex(i)}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === index ? "w-8 bg-primary" : "w-4 bg-muted-foreground/40 hover:bg-muted-foreground/60",
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
